<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onBeforeMount, onMounted, ref } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { CONSENT_REQUEST_STATUS, SOURCES_TYPES } from "@core/constants"
import { useAuthStore } from "@core/stores/auth.store"
import { coreServiceRegistry } from "@core/services/register"
import type { ConsentRequestInvitationDto } from "@types"

// ================================
// ROUTE & STORES
// ================================

const route = useRoute()
const authStore = useAuthStore()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            alert: "Alerte",
            loading: "Récupération des informations, veuillez patienter...",
            loading_refused: "Mise à jour de la demande, veuillez patienter...",
            expired:
                "Le lien a expiré car la demande est terminée ou bien elle n'existe plus.",
            refused:
                "La demande a bien été refusée. Vous pouvez quitter cette page.",
        },
    },
})

// ================================
// STATE
// ================================

type PagePhase = "loading" | "loading_refused" | "valid" | "error" | "refused"

const phase = ref<PagePhase>("loading")
const invitationRequest = ref<ConsentRequestInvitationDto | null>(null)

// ================================
// COMPUTED
// ================================

const requestId = computed(() => String(route.params.id ?? ""))

const loadingMessage = computed(() =>
    phase.value === "loading_refused" ? t("loading_refused") : t("loading")
)

// ================================
// LIFECYCLE
// ================================

onBeforeMount(() => {
    authStore.logout()
})

onMounted(() => {
    void runFlow()
})

// ================================
// FLOW
// ================================

async function runFlow(): Promise<void> {
    phase.value = "loading"
    const id = requestId.value
    const token =
        typeof route.query.token === "string" ? route.query.token : undefined
    const action =
        typeof route.query.action === "string" ? route.query.action : undefined

    try {
        const data =
            await coreServiceRegistry.consentRequest.getConsentRequestSign(
                id,
                token
            )

        if (data.token) {
            authStore.setToken(data.token)
            await authStore.fetchUser()
        }

        invitationRequest.value = data
        const st = data.state

        if (st === CONSENT_REQUEST_STATUS.CREATED && action === "refused") {
            phase.value = "loading_refused"
            await coreServiceRegistry.consentRequest.updateConsentRequestSign(
                id,
                { status: CONSENT_REQUEST_STATUS.REFUSED }
            )
            invitationRequest.value = {
                ...data,
                state: CONSENT_REQUEST_STATUS.REFUSED,
            }
            phase.value = "refused"
            return
        }

        if (
            st === CONSENT_REQUEST_STATUS.CREATED ||
            st === CONSENT_REQUEST_STATUS.ACCEPTED ||
            st === CONSENT_REQUEST_STATUS.WAITING_GRDF
        ) {
            phase.value = "valid"
            return
        }

        if (st === CONSENT_REQUEST_STATUS.REFUSED) {
            phase.value = "refused"
            return
        }

        phase.value = "error"
    } catch {
        phase.value = "error"
    }
}

function onFormDone(): void {
    /* Le titulaire a terminé le parcours ; la page peut rester affichée. */
}
</script>

<template>
    <div class="page-consent-sign-invitation flex min-h-screen w-full flex-col">
        <ConsentRequestScopeHeader />
        <div class="flex flex-1 justify-center items-start px-4 py-8">
            <div class="w-full max-w-3xl flex flex-col items-center gap-6">
                <template v-if="phase !== 'valid'">
                    <div
                        v-if="
                            phase === 'loading' || phase === 'loading_refused'
                        "
                        class="flex flex-col items-center gap-4 py-12"
                    >
                        <BaseProgressSpinner
                            style="width: 2rem; height: 2rem"
                        />
                        <p class="text-sm text-gray-600 text-center">
                            {{ loadingMessage }}
                        </p>
                    </div>
                    <div
                        v-else-if="phase === 'error'"
                        class="flex flex-col items-center gap-3 text-center max-w-md"
                    >
                        <h1
                            class="text-lg font-semibold text-gray-800 flex items-center gap-2"
                        >
                            {{ t("alert") }}
                            <i
                                class="pi pi-info-circle text-gray-500"
                                aria-hidden="true"
                            />
                        </h1>
                        <p class="text-sm text-amber-800">
                            {{ t("expired") }}
                        </p>
                    </div>
                    <div
                        v-else-if="phase === 'refused'"
                        class="flex flex-col items-center gap-3 text-center max-w-md"
                    >
                        <h1
                            class="text-lg font-semibold text-gray-800 flex items-center gap-2"
                        >
                            {{ t("alert") }}
                            <i
                                class="pi pi-info-circle text-gray-500"
                                aria-hidden="true"
                            />
                        </h1>
                        <p class="text-sm text-gray-700">
                            {{ t("refused") }}
                        </p>
                    </div>
                </template>

                <PdlFormOnInvitation
                    v-else-if="
                        invitationRequest?.source?.idtype === SOURCES_TYPES.PDL
                    "
                    :request="invitationRequest"
                    @done="onFormDone"
                />
                <PceFormOnInvitation
                    v-else-if="invitationRequest"
                    :request="invitationRequest"
                    @done="onFormDone"
                />
            </div>
        </div>
    </div>
</template>
